import { QueryOptions } from '@apollo/client/core';
import BaseService from '@root/common/base/BaseService';
import { ServiceInterface } from '@root/common/types/service';
import getPageBlockQuery from '@root/modules/feed/graphql/getPageBlockQuery.graphql';
import { dataLoader, RedisDataLoaderOptions } from '@root/libs/redis/dataloader/dataLoader';
import { BlockArticle } from '@root/modules/feed/types/pageContent';

type ServiceVariables = {
  id: string;
  authorLanguage?: string;
  getArticles?: boolean;
};

interface Block {
  id: string;
  type: string;
  attributes: Record<string, any>[];
  data?: Record<string, unknown>;
  blockArticles: {
    items: BlockArticle[];
  };
}

interface BlockData {
  blocks: Block[];
}

export default class GetPageBlockService extends BaseService implements ServiceInterface {
  private async fetchBlock(variables: ServiceVariables) {
    const options: QueryOptions<ServiceVariables> = Object.assign({ query: getPageBlockQuery }, { variables });

    const dataLoaderOptions: RedisDataLoaderOptions = {
      remote: {
        expireTimeMs: 30000, // 30 seconds
        gracePeriodMs: 24 * 3600 * 1000, // 24 hours
        keyPrefix: `page_block`,
      },
    };

    const requestWrapper = async (options: QueryOptions<ServiceVariables>): Promise<BlockData | Error> => {
      const apiProvider = this.createProvider('GraphQL');
      apiProvider.selectAPI('content-api-v3').setLinkOptions({ useAutomaticPersistedQueries: true, useGETAutomaticPersistedQueries: true });

      const [response] = await apiProvider.query<BlockData>(options);

      this.throwGraphqlOrApolloErrorIfExists(response);

      const isBlockNotFound = !response.data.blocks[0]?.id;

      // Throw error if no section found
      if (isBlockNotFound) {
        throw new Error('getPageBlock.service requestWrapper response error');
      }

      return response.data;
    };

    const redisDataLoader = dataLoader<QueryOptions<ServiceVariables>, BlockData | Error>(requestWrapper, dataLoaderOptions);
    const response: BlockData | Error = redisDataLoader ? await redisDataLoader.load(options) : await requestWrapper(options);

    if (response instanceof Error) {
      throw response;
    }

    return {
      blocks: response?.blocks || [],
    };
  }

  public async fetch(variables: ServiceVariables) {
    try {
      const response = await this.fetchBlock(variables);

      return {
        blocks: response.blocks,
      };
    } catch (error) {
      process.sentry?.captureException(`ConfigService fetch failed`, {
        contexts: { data: { variables }, ...(error.message && { message: error.message }), ...(error.cause && { cause: error.cause }) },
        tags: { 'process.type': process.server ? 'server' : 'client' },
      });
      this.handleError({ statusCode: 500, message: 'error.unexpected_system_error' });
    }
  }
}

export function fetchBlock(variables: ServiceVariables, { req }: { req: PortalRootIncomingMessage }) {
  const getPageBlockService = new GetPageBlockService({ req });
  return getPageBlockService.fetch(variables);
}
